<script lang="ts" setup>
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeLabel from "@dsk/components/Label/LeLabel.vue";
import LeFloatingContainer from "@dsk/components/LeFloatingContainer.vue";
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeChevronRight from "@dsk/icons/LeChevronRight.vue";
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { RouteLocation } from "vue-router";
import { useRoute, useRouter } from "vue-router";

import LeDictionaryList from "@/components/dictionaries/LeDictionaryList.vue";
import { useResponsiveness } from "@/composables";
import { useUserStore } from "@/stores";
import type { Dictionary, DictionaryListFilter, PrivateSubscriptionBox } from "@/types";

import LeSubscriptionDictionariesModal from "./LeSubscriptionDictionariesModal.vue";
import LeUspItem from "./LeUspItem.vue";

const props = defineProps<{
  allDictionaries: Dictionary[];
  subscription: PrivateSubscriptionBox;
}>();

const { t } = useI18n();
const { isAtLeastTabletScreen } = useResponsiveness();
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const isAvailable = computed<boolean>(() => props.subscription.product.availability === "available");
const isUnavailable = computed<boolean>(() => !isAvailable.value);
const hasPurchasedProduct = computed<boolean>(() => props.subscription.product.availability === "already-purchased");

const dictionariesLoaded = computed<boolean>(() => props.allDictionaries.length > 0);

const showDictionaries = ref<boolean>(false);
const dictionariesInPackage = computed<Dictionary[]>(() =>
  props.allDictionaries.filter((dictionary) => dictionary.tags.includes(`package-${props.subscription.product.id}`))
);

const purchaseRoute = computed<RouteLocation>(() => {
  if (isUnavailable.value) {
    return route;
  }

  const routeName = userStore.isSignedIn ? "subscription.private.confirm-payment" : "subscription.private.sign-up";

  return router.resolve({
    name: routeName,
    query: { package: props.subscription.product.id }
  });
});

const dictionaryListFilters = reactive<DictionaryListFilter[]>([
  {
    title: t("le-private-subscription-box.dictionary-list-filters.featured"),
    dictionaryTags: ["featured"]
  },
  {
    title: t("le-private-subscription-box.dictionary-list-filters.danish"),
    dictionaryTags: ["monoling-da"]
  },
  {
    title: t("le-private-subscription-box.dictionary-list-filters.english"),
    dictionaryTags: ["src-en", "dest-en"]
  },
  {
    title: t("le-private-subscription-box.dictionary-list-filters.german"),
    dictionaryTags: ["src-de", "dest-de"]
  },
  {
    title: t("le-private-subscription-box.dictionary-list-filters.french"),
    dictionaryTags: ["src-fr", "dest-fr"]
  },
  {
    title: t("le-private-subscription-box.dictionary-list-filters.all"),
    dictionaryTags: null,
    isDefault: true,
    isSearchable: true
  }
]);

const onShowDictionariesClick = () => {
  showDictionaries.value = !showDictionaries.value;
};

const onHideDictionaries = () => {
  showDictionaries.value = false;
};
</script>

<template>
  <LeFloatingContainer class="le-private-subscription-box" color="neutral">
    <div class="le-private-subscription-box__content">
      <div>
        <div class="le-private-subscription-box__top">
          <LeHeading class="le-private-subscription-box__title" tag="h2" size="l">{{
            subscription.product.name
          }}</LeHeading>
          <LeLabel size="l" :color="subscription.label.color">{{ subscription.label.text }}</LeLabel>
        </div>
        <ul class="list-unstyled mb-0">
          <LeUspItem v-for="(usp, index) in subscription.usps" :key="index">{{ usp }}</LeUspItem>
        </ul>
      </div>

      <div class="le-private-subscription-box__cta-button-container">
        <LeTooltip
          :hidden="isAvailable"
          :text="
            hasPurchasedProduct
              ? t('le-private-subscription-box.tooltip-already-purchased')
              : t('le-private-subscription-box.tooltip-not-available')
          "
          :margin-bottom="10"
          class="le-private-subscription-box__tooltip"
          trigger-container-class="le-private-subscription-box__tooltip-trigger-container"
        >
          <LeButton
            tag="router-link"
            size="large"
            variant="outline"
            :disabled="isUnavailable"
            full-width
            :to="purchaseRoute"
          >
            {{ t("le-private-subscription-box.cta-button-text", { product: subscription.product.name }) }}
          </LeButton>
        </LeTooltip>
      </div>

      <button
        type="button"
        class="le-private-subscription-box__included-dictionaries-button"
        :disabled="!dictionariesLoaded"
        @click="onShowDictionariesClick()"
      >
        <span>
          {{ t("le-private-subscription-box.included-dictionaries") }}
        </span>
        <LeChevronRight role="presentation" />
      </button>
    </div>

    <LeSubscriptionDictionariesModal
      v-if="isAtLeastTabletScreen && showDictionaries"
      :is-open="showDictionaries"
      :product="props.subscription.product"
      :dictionaries="dictionariesInPackage"
      :filters="dictionaryListFilters"
      @close="onHideDictionaries()"
    />
    <div v-else-if="showDictionaries" class="le-private-subscription-box__dictionary-list">
      <LeHeading class="le-private-subscription-box__dictionary-list-heading" tag="h3" size="m">
        {{ t("le-private-subscription-box.dictionary-list-title", { productName: props.subscription.product.name }) }}
      </LeHeading>
      <LeDictionaryList
        :dictionaries="dictionariesInPackage"
        :filters="dictionaryListFilters"
        show-descriptions-toggle
      />
    </div>
  </LeFloatingContainer>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-private-subscription-box {
  flex: 1 1 0;
  min-width: 345px;
  max-width: 360px;
  height: 100%;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    padding: 44px 22px;

    #media-breakpoint-up(lg, {
      padding-right: 30px;
      padding-left: 30px;
    });
  }

  &__top {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;
    text-align: center;
  }

  &__title {
    margin-bottom: 6px;
  }

  &__cta-button-container {
    width: 100%;
    margin-top: 24px;
  }

  &__tooltip,
  :deep(&__tooltip-trigger-container) {
    width: 100%;
  }

  &__included-dictionaries-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-top: 24px;
    border: none;
    border-radius: 4px;
    background: none;
    .body-text("s");
    .link();

    &.focus-visible {
      .focus-outline(green);
    }
  }

  &__dictionary-list {
    display: flex;
    flex-direction: column;
    padding: 26px;
    border-radius: 0 0 4px 4px;
    background: @colors[@secondary][100];

    &-heading {
      margin-bottom: 16px;
      text-align: center;
    }
  }
}
</style>
